import styled, { css } from 'styled-components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DOC_FORMAT_CONFIG_PREDEFINED } from '../../define/global';
import {
  NAVERWORKS_NAVIGATOR_TYPE,
  NAVERWORKS_SUPPORT_IMAGE_EXT
} from '../../define/naverworksDefine';

import { getExtension } from '../../utils/FileControlUtil';
import { isDevicePC } from '../../utils/util';
import { getNaverWorksFile } from '../../extApis/naverWorksApi';
import { CustomScrollbar, noTextSelect } from '../common/CssCommon';
import { useAppDataSliceSelector } from '../../store/modules/appData';
import ProgressPopup from '../common/ProgressPopup';
import IconSpinner from '../../img/spinner.gif';
import ListView from './ListView';
import { isFolder } from '../../utils/NaverWorksUtil';
import ThumbnailView from './ThumbnailView';

import IconSortDesc from '../../img/ico_sort_dn.png';
import IconSortDesc2x from '../../img/ico_sort_dn@2x.png';
import IconSortAsc from '../../img/ico_sort_up.png';
import IconSortAsc2x from '../../img/ico_sort_up@2x.png';
import { useEffect } from 'react';

const PADDING_SIZE = 9;
const IMAGE_ICON_WIDTH = 36.5;
const ICON_RIGHT_MARGIN = 11.5;

const HeaderButtonCss = css`
  cursor: pointer;
  ${noTextSelect}
`;

const Wrapper = styled.div`
  width: 100%;
  height: 482px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding-bottom: 8px;

  ${({ isMobile }) =>
    isMobile &&
    css`
      height: 100%;
    `}
`;
const NavigatorHeader = styled.div`
  width: 100%;
  height: 36px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  border-bottom: 1px solid #b1b6bb;
  box-sizing: border-box;
  padding: 0 ${PADDING_SIZE + 9}px 0 ${PADDING_SIZE + IMAGE_ICON_WIDTH + ICON_RIGHT_MARGIN}px;
  font-size: 14px;
  color: #2f3133;
  overflow: hidden;
`;

const HeaderName = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  ${HeaderButtonCss}
`;

const HeaderModifyInfo = styled.div`
  width: 111px;
  display: flex;
  align-items: center;
  font-size: 12px;
  ${HeaderButtonCss}
  ${({ isHeader }) =>
    isHeader &&
    css`
      font-size: 14px;
    `}
`;

const HeaderSize = styled.div`
  width: 70px;
  display: flex;
  align-items: center;
  font-size: 12px;
  ${HeaderButtonCss}
  ${({ isHeader }) =>
    isHeader &&
    css`
      font-size: 14px;
    `}
`;

const NavigatorContents = styled.div`
  flex: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 8px ${PADDING_SIZE}px;
  box-sizing: border-box;
  overflow-y: scroll;
  ${CustomScrollbar}
`;

const SpinnerArea = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  font-size: 15px;
  color: #797e82;
  white-space: nowrap;
  overflow: hidden;
`;

const ArrangeIcon = styled.img`
  width: 16px;
  height: 16px;
  margin-left: 10px;
`;

const HEADER_FIELD = {
  FILE_NAME: 'fileName',
  MODIFY_DATE: 'modifiedTime',
  FILE_SIZE: 'fileSize'
};

export default function NaverWorksFileNavigator({
  isComplete,
  fileList,
  onClickFolder,
  onClickFile,
  navigatorType = NAVERWORKS_NAVIGATOR_TYPE.DOCUMENT
}) {
  const { t } = useTranslation();
  const appDataSlice = useAppDataSliceSelector();
  const isPCView = isDevicePC(appDataSlice.deviceType);
  const [isDownloading, setIsDownloading] = useState(false);
  const [displayFileList, setDisplayFileList] = useState([]);
  const [arrangeType, setArrangeType] = useState({
    field: HEADER_FIELD.MODIFY_DATE,
    isAsc: false
  });
  const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });

  useEffect(() => {
    setDisplayFileList(
      arrange(
        fileList.filter((file) => isFolder(file.fileType) || checkSupportExt(file.fileName)),
        arrangeType.field,
        arrangeType.isAsc
      )
    );
  }, [fileList]);

  useEffect(() => {
    setDisplayFileList((prevState) => arrange(prevState, arrangeType.field, arrangeType.isAsc));
  }, [arrangeType]);

  const isMobile = isPCView === false;

  const checkSupportExt = (fileName) => {
    let supportExtArr = [];
    if (navigatorType === NAVERWORKS_NAVIGATOR_TYPE.IMAGE) {
      supportExtArr = Object.keys(NAVERWORKS_SUPPORT_IMAGE_EXT);
    } else {
      for (const key in DOC_FORMAT_CONFIG_PREDEFINED) {
        supportExtArr = [...supportExtArr, ...DOC_FORMAT_CONFIG_PREDEFINED[key].extList];
      }
    }

    return supportExtArr.includes(getExtension(fileName).toLowerCase());
  };

  const arraySort = (a, b, field, isAsc) =>
    isAsc ? collator.compare(a[field], b[field]) : collator.compare(b[field], a[field]);

  const arrange = (array, field, isAsc) => {
    const newFolderList = array
      .filter((file) => isFolder(file.fileType))
      .sort((a, b) => arraySort(a, b, field, isAsc));
    const newFileList = array
      .filter((file) => !isFolder(file.fileType))
      .sort((a, b) => arraySort(a, b, field, isAsc));
    return [...newFolderList, ...newFileList];
  };

  const openDocument = async (type, driveId, fileId) => {
    setIsDownloading(true);
    getNaverWorksFile(type, driveId, fileId)
      .then(({ file }) => {
        setIsDownloading(false);
        onClickFile(file, type, driveId, fileId);
      })
      .catch(() => {
        alert('Document open Failed.');
        setIsDownloading(false);
      });
  };

  const getFileList = (array, isMobile, navigatorType) => {
    return (
      <>
        {navigatorType === NAVERWORKS_NAVIGATOR_TYPE.IMAGE ? (
          <ThumbnailView
            filteringList={array}
            isMobile={isMobile}
            onClickFolder={onClickFolder}
            openDocument={openDocument}
          />
        ) : (
          <ListView
            filteringList={array}
            isMobile={isMobile}
            onClickFolder={onClickFolder}
            openDocument={openDocument}
          />
        )}
      </>
    );
  };

  return (
    <>
      <Wrapper isMobile={isMobile}>
        {isPCView && (
          <NavigatorHeader>
            <HeaderName
              onClick={() => {
                setArrangeType({
                  field: HEADER_FIELD.FILE_NAME,
                  isAsc: arrangeType.field !== HEADER_FIELD.FILE_NAME ? true : !arrangeType.isAsc
                });
              }}>
              {t('NaverWorksFileNavigator.Name')}
              {arrangeType.field === HEADER_FIELD.FILE_NAME && (
                <ArrangeIcon
                  src={arrangeType.isAsc ? IconSortAsc : IconSortDesc}
                  srcSet={arrangeType.isAsc ? IconSortAsc2x : IconSortDesc2x}
                />
              )}
            </HeaderName>
            <HeaderModifyInfo
              isHeader={true}
              onClick={() => {
                setArrangeType({
                  field: HEADER_FIELD.MODIFY_DATE,
                  isAsc: arrangeType.field !== HEADER_FIELD.MODIFY_DATE ? true : !arrangeType.isAsc
                });
              }}>
              {t('NaverWorksFileNavigator.ModifyDate')}
              {arrangeType.field === HEADER_FIELD.MODIFY_DATE && (
                <ArrangeIcon
                  src={arrangeType.isAsc ? IconSortAsc : IconSortDesc}
                  srcSet={arrangeType.isAsc ? IconSortAsc2x : IconSortDesc2x}
                />
              )}
            </HeaderModifyInfo>
            <HeaderSize
              isHeader={true}
              onClick={() => {
                setArrangeType({
                  field: HEADER_FIELD.FILE_SIZE,
                  isAsc: arrangeType.field !== HEADER_FIELD.FILE_SIZE ? true : !arrangeType.isAsc
                });
              }}>
              {t('NaverWorksFileNavigator.Size')}
              {arrangeType.field === HEADER_FIELD.FILE_SIZE && (
                <ArrangeIcon
                  src={arrangeType.isAsc ? IconSortAsc : IconSortDesc}
                  srcSet={arrangeType.isAsc ? IconSortAsc2x : IconSortDesc2x}
                />
              )}
            </HeaderSize>
          </NavigatorHeader>
        )}
        <NavigatorContents>
          {isComplete ? (
            <>
              {displayFileList && displayFileList.length > 0 ? (
                <>{getFileList(displayFileList, isMobile, navigatorType)}</>
              ) : (
                <SpinnerArea>{t('NaverWorksFileNavigator.EmptyFolder')}</SpinnerArea>
              )}
            </>
          ) : (
            <SpinnerArea>
              <img src={IconSpinner} />
            </SpinnerArea>
          )}
        </NavigatorContents>
      </Wrapper>
      {isDownloading && (
        <ProgressPopup
          isMobile={isMobile}
          message={
            navigatorType === NAVERWORKS_NAVIGATOR_TYPE.IMAGE
              ? t('NaverWorksFileNavigator.ImageProgressPopupMessage')
              : t('NaverWorksFileNavigator.DocumentProgressPopupMessage')
          }
        />
      )}
    </>
  );
}
